import FieldTypes from '../constants/FieldTypes';
import { isEmptyValue } from './validation';
import { getPercentageAdjustedValue, reversePercentageAdjustedValue } from './documentHelper';

/**
 * Auto-height helper
 *
 * Pure calculator for the autoHeight feature, the opposite knob to autoShrink: where that one
 * makes the font smaller so the value fits a fixed box, this one makes the box fit the value at
 * the size it is. It grows on any blur and, after an edit, shrinks as well, never below one line
 * of text. Knows nothing about React or the DOM beyond the nodes it is handed to measure, so the
 * page components can each state their own rounding policy and get an answer in the units they
 * persist.
 *
 * Three pieces:
 *   measureAutoHeight            reads what a value needs and what its box gives, in pixels
 *   createMeasurementSurrogate   a hidden textarea that wraps like the editor, for measuring
 *                                after the editor is gone
 *   getAutoHeightPositionChanges turns a measurement into the height to persist, with a reason
 *                                when there is nothing to persist
 *
 * The PDF exporter uses the first and the last with shrink switched off: an export never
 * shrinks a box (see resolveAutoHeightExportChanges).
 */

//The lineHeight percentage a fillable PDF textarea falls back to when the document has none
const DEFAULT_LINE_HEIGHT_PERCENT = 10;

/**
 * scrollHeight and clientHeight are integers, so a measured need can understate the real one by
 * up to one screen pixel. On PDF pages the target is padded by this much, which guarantees the
 * next blur measures no overflow. Row pages need no padding: their boxes and line heights are
 * whole pixels, and whole rows are added anyway.
 */
const PIXEL_PAGE_SAFETY_PX = 1;

/**
 * On PDF pages a box that is at most this much taller than its value is left alone. After any
 * change the spare is the safety pixel plus, on a fillable PDF textarea, the rounding of the
 * rescaled line pitch. Without this band a second blur could shave a fraction of a pixel and a
 * third could put it back. Well under one line (6px minimum font x 1.2), so it never hides text.
 */
const PIXEL_PAGE_SETTLE_PX = 2;

/**
 * How long a field waits before measuring again after a 'retry' answer: react-grid-layout
 * animates an item's height over 200ms, plus a margin. And how many times it tries before
 * leaving the request for the next blur.
 */
export const AUTO_HEIGHT_RETRY_DELAY_MS = 250;
export const AUTO_HEIGHT_MAX_RETRIES = 3;

/**
 * A value as the editor shows it. A never-filled textarea has an undefined value and an empty
 * DOM string, and comparing those raw would count a blank box as edited on its first
 * click-through. Both fields compare through this.
 *
 * @param {*} value
 * @returns {String}
 */
export const asText = (value) => (value === null || value === undefined ? '' : String(value));

//Two-decimal helpers. toFixed(6) first absorbs binary float noise such as 80.480000000001.
const round2 = (v) => Number(v.toFixed(2));
const ceil2 = (v) => Math.ceil(Number((v * 100).toFixed(6))) / 100;

//A computed style length as a number; anything unparseable counts as 0
const px = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

/**
 * The single field-type gate for this feature, mirroring resolveAutoShrinkConfig.
 *
 * Tested for exactly true, rather than merely truthy, so a stray string left on an older
 * document can not switch the feature on. Text and textarea only: TextField also renders
 * number and uniqueId fields, and those keep their existing fixed height.
 *
 * @param {Object} params
 * @param {String} params.type //Field type
 * @param {Boolean} params.autoHeight
 * @returns {Boolean}
 */
export const resolveAutoHeightEnabled = ({ type, autoHeight }) => (
  autoHeight === true && (type === FieldTypes.text || type === FieldTypes.textarea)
);

/**
 * The slot a node was given, in border-box pixels.
 *
 * A node pinned with height: 100% (or flex: 1) fills its slot exactly, so its own offsetHeight is
 * the answer. But a node can be taller than its slot: a border-box element can never be shorter
 * than its padding plus border, and a textarea that is a flex item keeps its intrinsic minimum
 * (two rows by default) whatever its height says. Then it overhangs, the outer wrapper clips
 * it, and offsetHeight reports the node rather than the slot. So the parent is asked as well,
 * its inner height less every in-flow sibling (the title row), and the smaller of the two is
 * the room. Absolute siblings take no room and are skipped: the tooltip wrapper, the
 * fillable-PDF background and rule layers, and the measurement surrogate.
 *
 * @param {Object} node
 * @param {Number} boxPx //node.offsetHeight
 * @returns {Number}
 */
const resolveRoomPx = (node, boxPx) => {

  const parent = node.parentElement;

  if (!parent) return boxPx;

  const used = Array.from(parent.children).reduce((sum, child) => {
    if (child === node) return sum;
    const position = window.getComputedStyle(child).position;
    return (position === 'absolute' || position === 'fixed') ? sum : sum + child.offsetHeight;
  }, 0);

  return Math.max(0, Math.min(boxPx, parent.clientHeight - used));

};

/**
 * Read what a value needs and what its box gives.
 *
 * Called synchronously from onBlur, before the editor is swapped for the display node, and
 * again from a retry with the display node as the box and a surrogate as the text (see
 * createMeasurementSurrogate). The exporter calls it on the display node in its measuring pass.
 *
 * Everything about the box is read from `node` before anything is touched. The text is then
 * read from `contentNode` (or `node` itself) by collapsing it to zero height, so scrollHeight
 * reports the text plus its vertical padding rather than max(text, box), and restoring its
 * inline styles. Same task, no paint in between, so nothing flashes.
 *
 * Never returns null for a node that exists: the exporter waits for every measurable field to
 * report, and a missing report would stall the export.
 *
 * @param {Object} node //The pinned element whose slot is the box: the editor at blur, the display on retry
 * @param {*} value //The value as it stands at blur
 * @param {String} type //Field type, for the empty check
 * @param {Number} heightAtMeasure //The field's height prop at the moment of measuring
 * @param {Object} [options]
 * @param {Boolean} [options.edited] //Whether this blur may shrink: the value changed during the focus
 * @param {Object} [options.contentNode] //Where to read the text from, when not the box node
 * @returns {Object|null}
 */
export const measureAutoHeight = (node, value, type, heightAtMeasure, { edited = false, contentNode = null } = {}) => {

  if (!node || typeof node.scrollHeight !== 'number') return null;

  const textNode = contentNode || node;

  const empty = isEmptyValue(value, type);

  const style = window.getComputedStyle(textNode);

  const paddingPx = px(style.paddingTop) + px(style.paddingBottom);
  const borderPx = px(style.borderTopWidth) + px(style.borderBottomWidth);

  /**
   * Every auto-height editor sets an explicit pixel line-height. A fillable PDF textarea reports
   * 0px until its wrapper has been measured (its pitch is a percentage of that wrapper), and a
   * one-line height built on 0 would be nonsense. With no usable pitch the field may still grow;
   * it may not shrink.
   */
  const lineHeightPx = parseFloat(style.lineHeight);
  const hasLine = Number.isFinite(lineHeightPx) && lineHeightPx > 0;

  //Everything tied to the drawn box, read before the box is touched
  const boxHeightPx = node.offsetHeight;
  const widthBefore = node.offsetWidth;

  /**
   * Today's reading, kept for the exporter: scrollHeight less clientHeight with the scrollbar
   * hidden. The exporter's grow-only arithmetic is fed this number so an exported box lands on
   * exactly the pixel it did before, fractional borders included, where the ceil() below could
   * move it by one.
   */
  const previousOverflowY = textNode.style.overflowY;
  textNode.style.overflowY = 'hidden';
  const legacyOverflowPx = Math.max(0, textNode.scrollHeight - textNode.clientHeight);
  textNode.style.overflowY = previousOverflowY;

  const roomPx = resolveRoomPx(node, boxHeightPx);

  /**
   * The grid item the node sits in, so the page can tell a settled box from one that
   * react-grid-layout is still animating. Item and slot animate together, so their difference,
   * the chrome (grid border, margin padding, title row), is the same number mid-animation as at
   * rest. That is what lets the calculator work from an absolute target.
   */
  const item = node.closest ? node.closest('.react-grid-item') : null;
  const itemPx = item ? item.offsetHeight : undefined;
  const chromePx = Number.isFinite(itemPx) ? Math.max(0, itemPx - roomPx) : undefined;

  /**
   * Collapse and read. overflowY hidden: a classic scrollbar, the kind Windows and Linux draw,
   * takes width from inside the box and pushes an extra wrap onto the text, and the reader will
   * not have one once the box fits. flex 0 0 auto: TextField's editor is flex: 1 and would
   * ignore the height otherwise. Restored through cssText whatever happens.
   */
  const previousCss = textNode.style.cssText;

  let contentPx = 0;
  let widthStable = false;

  try {
    textNode.style.overflowY = 'hidden';
    textNode.style.minHeight = '0px';
    textNode.style.flex = '0 0 auto';
    textNode.style.height = '0px';
    contentPx = textNode.scrollHeight;
    widthStable = textNode.offsetWidth === widthBefore;
  } finally {
    textNode.style.cssText = previousCss;
  }

  //One line of text inside the padding; the floor for every value, and the whole need for an empty one
  const oneLinePx = (hasLine ? lineHeightPx : 0) + paddingPx;

  const needPx = Math.ceil((empty ? oneLinePx : Math.max(contentPx, oneLinePx)) + borderPx);

  //Shrinking needs permission, a known line pitch, and a text node that wrapped at the box's own width
  const canShrink = edited && hasLine && widthStable;

  return {
    needPx,
    roomPx,
    chromePx,
    overflowPx: Math.max(0, needPx - roomPx),
    sparePx: canShrink ? Math.max(0, roomPx - needPx) : 0,
    legacyOverflowPx,

    /**
     * offsetHeight on purpose. It is the same integer the fillable PDF renderer derives its ruled
     * line pitch from, so the percentage persisted here and the pitch drawn on screen come from
     * one number. getBoundingClientRect would report the exporter's CSS page transform as well,
     * which offsetHeight does not.
     */
    boxHeightPx,
    itemPx,
    empty,

    /**
     * The height prop the field was drawn with when it measured itself, in whatever unit the
     * page handed it. The page compares it with the document before persisting, so a
     * measurement taken against a box that has since been re-sized is dropped rather than
     * added on top of the new size.
     */
    heightAtMeasure,
  };

};

//Typography that decides where text wraps, copied from the display node onto the surrogate
const SURROGATE_STYLE_KEYS = [
  'paddingTop', 'paddingBottom', 'paddingLeft', 'paddingRight',
  'borderTopWidth', 'borderBottomWidth', 'borderLeftWidth', 'borderRightWidth',
  'fontFamily', 'fontSize', 'fontWeight', 'fontStyle', 'fontVariant', 'fontStretch',
  'letterSpacing', 'wordSpacing', 'textTransform', 'textIndent', 'tabSize', 'direction',
];

/**
 * A hidden textarea that wraps exactly like the editor would, for measuring after the editor
 * is gone.
 *
 * The display node is not a stand-in for the editor: an auto-shrink display is clamped to its
 * line limit, so it can only ever measure the lines it shows, and a pre-line display does not
 * draw the trailing newline the editor draws. A textarea gets the editor's own whitespace rules
 * from the browser (pre-wrap, break-word; neither editor overrides them), is never clamped, and
 * counts a trailing newline the way the editor does. white-space, word-break and overflow-wrap
 * are therefore deliberately not copied from the display.
 *
 * Mounted beside the display node, absolutely positioned and hidden, so it takes no room and
 * resolveRoomPx skips it. Sized to the display's width, which is the editor's width: both fill
 * the same slot. The caller removes it in a finally.
 *
 * @param {Object} boxNode //The display node
 * @param {Object} params
 * @param {Number} params.lineHeightPx //The pitch the editor draws at
 * @param {*} params.value //The value to measure
 * @returns {Object|null} //The surrogate, appended, or null when there is nowhere to mount it
 */
export const createMeasurementSurrogate = (boxNode, { lineHeightPx, value }) => {

  const doc = boxNode && boxNode.ownerDocument;
  const parent = boxNode && boxNode.parentElement;

  if (!doc || !parent) return null;

  const box = window.getComputedStyle(boxNode);

  const surrogate = doc.createElement('textarea');

  surrogate.setAttribute('aria-hidden', 'true');
  surrogate.setAttribute('data-auto-height-surrogate', 'true');
  surrogate.tabIndex = -1;
  surrogate.readOnly = true;

  const styles = {
    position: 'absolute',
    top: '0',
    left: '0',
    visibility: 'hidden',
    pointerEvents: 'none',
    boxSizing: 'border-box',
    width: `${boxNode.offsetWidth}px`,
    height: '0px',
    minHeight: '0px',
    margin: '0',
    resize: 'none',
    overflowY: 'hidden',
    borderStyle: 'solid',
    borderColor: 'transparent',
    lineHeight: `${lineHeightPx}px`,
  };

  SURROGATE_STYLE_KEYS.forEach((key) => { styles[key] = box[key]; });

  Object.assign(surrogate.style, styles);

  surrogate.value = value === null || value === undefined ? '' : String(value);

  parent.appendChild(surrogate);

  return surrogate;

};

/**
 * Work out what to persist on the fieldPosition after a measurement.
 *
 * Units are always "rows of rowHeight px": react-grid-layout draws every item h * rowHeight
 * pixels tall on every page kind (its margin is 0 here). PDF pages created by the app use
 * rowHeight 1, which is why their heights read as design pixels, but nothing here assumes that.
 *
 * snapToRows is the page's statement of how to round, never inferred from rowHeight. FormPage
 * passes true and gets whole rows; PDFPage passes false and gets two decimals plus the safety
 * pixel.
 *
 * The target is absolute: what the value needs plus the chrome around it. It does not depend on
 * the box the DOM happens to be showing, so the same value always resolves to the same stored
 * height, and a second blur with that value has nothing to do.
 *
 * Returns { changes, reason }. changes is the object to persist, or null, in which case reason
 * says why: 'settled' (nothing to do) or 'retry' (the box was still animating; measure again
 * once it has settled). A change carries reason 'applied'.
 *
 * @param {Object} params
 * @param {Object} params.fieldPosition //The unscaled, persisted fieldPosition
 * @param {Object} params.measurement //From measureAutoHeight
 * @param {Boolean} params.snapToRows
 * @param {Number} params.rowHeight
 * @param {Number} params.percentageDifference //The page's viewport scaling, 0 on grid pages
 * @param {Boolean} params.keepLinePitch //Fillable PDF textareas only
 * @param {Boolean} params.shrink //false for the exporter, which only ever grows
 * @returns {{ changes: Object|null, reason: String }}
 */
export const getAutoHeightPositionChanges = ({
  fieldPosition,
  measurement,
  snapToRows,
  rowHeight = 1,
  percentageDifference = 0,
  keepLinePitch = false,
  shrink = true,
}) => {

  const settled = { changes: null, reason: 'settled' };

  if (!fieldPosition || !measurement || !Number.isFinite(fieldPosition.height) || !(rowHeight > 0)) return settled;

  const scale = 1 + percentageDifference;

  if (!Number.isFinite(scale) || scale <= 0) return settled;

  const { height, baseHeight, baseLineHeight } = fieldPosition;

  const lineHeight = Number.isFinite(fieldPosition.lineHeight) ? fieldPosition.lineHeight : DEFAULT_LINE_HEIGHT_PERCENT;

  /**
   * The renderer's conversion, reproduced in the renderer's own order: PDFPage scales the
   * stored height to two decimals, then react-grid-layout multiplies by rowHeight and rounds to
   * a whole pixel (calcGridItemWHPx). Scaling the product instead rounds in a different place
   * and drifts whenever rowHeight is not 1.
   */
  const toScreen = (units) => getPercentageAdjustedValue(units, percentageDifference) * rowHeight;
  const drawnPx = (units) => Math.round(toScreen(units));

  const currentPx = drawnPx(height);

  /**
   * The ruled line pitch is the input box in pixels times lineHeight over 100. Keeping the
   * pitch constant across a change therefore means scaling the percentage by old box over new
   * box. The measured box is used rather than the drawn height so a visible title does not
   * skew it.
   */
  const rescaleLineHeight = (nextBox) => {

    if (!(measurement.boxHeightPx > 0) || !(nextBox > 0)) return lineHeight;

    return round2((lineHeight * measurement.boxHeightPx) / nextBox);

  };

  const finish = (nextHeight, nextBox) => {

    const changes = { height: nextHeight };

    //Lazy capture for documents that were switched on without one; the settings panel reads these
    if (!Number.isFinite(baseHeight)) changes.baseHeight = height;

    if (keepLinePitch) {
      changes.lineHeight = rescaleLineHeight(nextBox);
      if (!Number.isFinite(baseLineHeight)) changes.baseLineHeight = lineHeight;
    }

    return { changes, reason: 'applied' };

  };

  /**
   * Grow only, today's arithmetic, untouched. The exporter takes this branch: an exported box
   * must land on exactly the pixel it did before this helper learned to shrink.
   */
  if (!shrink) {

    if (!(measurement.overflowPx > 0)) return settled;

    let nextHeight;

    if (snapToRows) {

      //Grid page: exact integer metrics, so a whole row always covers the overflow
      const overflowDesignPx = reversePercentageAdjustedValue(measurement.overflowPx, percentageDifference);

      nextHeight = round2(height + Math.ceil(overflowDesignPx / rowHeight));

    } else {

      /**
       * PDF page. Solve the renderer's predicate directly rather than inverting the formula and
       * hoping the roundings cancel:
       *   toScreen(next) - toScreen(height) >= paddedOverflowPx
       * requiredScaled is the smallest 2dp scaled value that satisfies the right-hand side, and
       * dividing by scale (rounded up to 2dp) gives a stored height whose scaled value reaches it.
       */
      const paddedOverflowPx = measurement.overflowPx + PIXEL_PAGE_SAFETY_PX;
      const currentScaled = getPercentageAdjustedValue(height, percentageDifference);
      const requiredScaled = ceil2(currentScaled + paddedOverflowPx / rowHeight);

      nextHeight = ceil2(requiredScaled / scale);

      /**
       * Confirm with the same arithmetic the renderer runs. Each 0.01-unit step moves next * scale
       * by 0.01 * scale, so within ceil(1 / scale) steps the 2dp scaled value must rise by at
       * least 0.01: that is the bound, derived from the page scale, not a fixed number.
       */
      const maxNudges = Math.ceil(1 / scale) + 2;

      let nudges = 0;

      while ((toScreen(nextHeight) - toScreen(height)) < paddedOverflowPx && nudges < maxNudges) {
        nextHeight = round2(nextHeight + 0.01);
        nudges += 1;
      }

      //Unreachable by construction. If it ever happens, refuse rather than persist a height that is too small.
      if ((toScreen(nextHeight) - toScreen(height)) < paddedOverflowPx) {
        // eslint-disable-next-line no-console
        console.warn('autoHeight: could not find a stored height that grows the rendered box enough; no change persisted');
        return settled;
      }

    }

    return finish(nextHeight, measurement.boxHeightPx + (toScreen(nextHeight) - toScreen(height)));

  }

  /**
   * Mid-transition: the document already carries the new height but react-grid-layout is still
   * animating the item to it (200ms), and a fillable textarea's pitch lags that. A spare read
   * now would be fiction and a shrink built on it could cut the text. Ask the field to measure
   * again once the box has settled.
   */
  if (Number.isFinite(measurement.itemPx) && measurement.itemPx !== currentPx) return { changes: null, reason: 'retry' };

  const overflowPx = measurement.overflowPx > 0 ? measurement.overflowPx : 0;
  const sparePx = measurement.sparePx > 0 ? measurement.sparePx : 0;

  if (!overflowPx && (snapToRows ? !sparePx : sparePx <= PIXEL_PAGE_SETTLE_PX)) return settled;

  /**
   * The absolute target in drawn pixels: what the value needs plus the chrome around it, plus
   * the safety pixel on pixel pages. chromePx is measured; the relative form is the fallback for
   * a node that is not inside a grid item.
   */
  const chromePx = Number.isFinite(measurement.chromePx) ? measurement.chromePx : currentPx - measurement.roomPx;
  const targetPx = measurement.needPx + chromePx + (snapToRows ? 0 : PIXEL_PAGE_SAFETY_PX);

  let nextHeight;

  if (snapToRows) {

    /**
     * Grid page: whole rows. Exact for an integer rowHeight; the two loops confirm against the
     * renderer's own rounding for any other.
     */
    let rows = Math.ceil((targetPx - currentPx) / rowHeight);

    for (let i = 0; i < 2 && drawnPx(height + rows) < targetPx; i += 1) rows += 1;
    for (let i = 0; i < 2 && drawnPx(height + rows - 1) >= targetPx; i += 1) rows -= 1;

    nextHeight = Math.max(1, round2(height + rows));

  } else {

    /**
     * PDF page. Math.round(x) >= T holds exactly when x >= T - 0.5, so the smallest 2dp stored
     * height whose drawn box reaches the target is analytic. The two loops make it a check rather
     * than a belief, as the grow-only branch's nudge loop does: within ceil(1 / scale) steps of
     * 0.01 the drawn pixel must move.
     */
    nextHeight = ceil2(ceil2((targetPx - 0.5) / rowHeight) / scale);

    const maxSteps = Math.ceil(1 / scale) + 2;

    for (let i = 0; i < maxSteps && drawnPx(nextHeight) < targetPx; i += 1) nextHeight = round2(nextHeight + 0.01);
    for (let i = 0; i < maxSteps && nextHeight > 0.01 && drawnPx(round2(nextHeight - 0.01)) >= targetPx; i += 1) nextHeight = round2(nextHeight - 0.01);

    //Unreachable by construction. If it ever happens, refuse rather than persist a height that is too small.
    if (drawnPx(nextHeight) < targetPx) {
      // eslint-disable-next-line no-console
      console.warn('autoHeight: could not find a stored height whose rendered box fits the value; no change persisted');
      return settled;
    }

  }

  if (!(nextHeight > 0)) return settled;

  //Same stored height, or a different one that draws the same pixels: nothing would change on screen
  if (nextHeight === height || drawnPx(nextHeight) === currentPx) return settled;

  return finish(nextHeight, measurement.boxHeightPx + (drawnPx(nextHeight) - currentPx));

};

export default resolveAutoHeightEnabled;
